import { cell, textCell } from "../../StationPerformance/utils/csv";
import { formatBerlin } from "./thgFormat";
import type { ThgAnnualSummary } from "../../../types/thgAnnualSummary";
import { COCHARGE_REVENUE_TEXTS } from "../../StationPerformance/StationPerformance.consts";
import { COPY } from "../ThgAnnualSummary.consts";
import { exportedRows } from "./thgInternal";
import { hasPayoutData, payoutExportStatus } from "./thgPayout";

/**
 * Written for German-locale Excel, which finance uses: `;` between fields and a decimal comma, so the
 * file opens correctly on a double-click and every figure is read as the number it is. A decimal point
 * would be read there as a thousands separator, turning 30.000 kWh into thirty thousand.
 */
export const THG_CSV_OPTIONS = { bom: true, separator: ";" } as const;

/**
 * A backend figure at its fixed precision with a decimal comma: 3831.394 → "3831,394". No thousands
 * separator and no currency sign, so Excel reads it as a number. The value itself is never re-rounded:
 * the backend already sends 3 (kWh) or 2 (EUR) decimals.
 */
const decimal = (value: number, digits: number): string => value.toFixed(digits).replace(".", ",");

/** The rate as configured (40, 75, or 40,5 if ever so), for headers and context. */
const rate = (value: number): string => String(value).replace(".", ",");

/**
 * Finance's payout sheet, in the column order they already use:
 *   # | Name | E-Mail | Stationsnummer | Erste Transaktion | Energie {year} CDR (kWh) | Geld MWh = {rate} (EUR)
 * followed, when the year carries CLR-283 payout data, by `Status Gutschrift | Gutschriftsnummer`.
 *
 * The energy column is the energy EXCLUDING free charging, because the money column is derived from it
 * (3831,394 kWh × 40 / 1000 = 153,26). Context rows go first, because a payout file must say which year,
 * rate and computation it came from, and a provisional file must say so inside itself, not only in its name.
 */
export const buildThgCsvRows = (summary: ThgAnnualSummary): string[][] => {
  const provisional = summary.status === "PROVISIONAL";
  // CLR-283: status and credit note number go after finance's seven columns, only when the year has them.
  const payouts = hasPayoutData(summary);
  const rows: string[][] = [
    [cell("THG payouts")],
    [cell("Year"), cell(summary.year)],
    [cell("Status"), cell(summary.status)],
    [cell("Rate (EUR/MWh)"), cell(rate(summary.rateEurPerMwh))],
    [cell("Computed at (Europe/Berlin)"), cell(formatBerlin(summary.computedAt))],
    [cell("Computation ID"), cell(summary.computationId ?? "")],
  ];
  if (provisional) rows.push([cell("Note"), cell(COPY.provisionalCsvNote)]);
  if (summary.stationsWithoutOwner > 0) {
    rows.push([cell("Stations without owner"), cell(summary.stationsWithoutOwner)]);
  }
  if (summary.excludedSessionCount > 0) {
    rows.push([cell("Excluded sessions"), cell(summary.excludedSessionCount)]);
  }
  // CLR-296: internal stations are not paid, so they are not on finance's sheet; # runs 1..n without gaps,
  // and the total is the sum of the rows that are on it.
  const { stations: payable, excluded, paidEnergyKwh, payoutEur } = exportedRows(summary);
  if (excluded > 0) rows.push([cell(COCHARGE_REVENUE_TEXTS.excludedInternal(excluded))]);
  rows.push([]);

  rows.push([
    cell("#"),
    cell("Name"),
    cell("E-Mail"),
    cell("Stationsnummer"),
    cell("Erste Transaktion"),
    cell(`Energie ${summary.year} CDR (kWh)`),
    cell(`Geld MWh = ${rate(summary.rateEurPerMwh)} (EUR)`),
    ...(payouts ? [cell("Status Gutschrift"), cell("Gutschriftsnummer")] : []),
  ]);

  payable.forEach((station, index) => {
    rows.push([
      cell(index + 1),
      textCell(station.owner?.name ?? ""),
      textCell(station.owner?.email ?? ""),
      textCell(station.stationId),
      cell(station.firstTransactionYear ?? ""),
      cell(decimal(station.paidEnergyKwh, 3)),
      cell(decimal(station.payoutEur, 2)),
      ...(payouts
        ? [cell(payoutExportStatus(station.payout)), textCell(station.payout?.creditNote?.number ?? "")]
        : []),
    ]);
  });

  rows.push([
    cell("Total"),
    cell(""),
    cell(""),
    cell(""),
    cell(""),
    cell(decimal(paidEnergyKwh, 3)),
    cell(decimal(payoutEur, 2)),
    ...(payouts ? [cell(""), cell("")] : []),
  ]);

  return rows;
};

export const thgCsvFilename = (summary: Pick<ThgAnnualSummary, "year" | "status">): string =>
  `thg-payouts-${summary.year}-${summary.status}.csv`;
